import React from 'react';
import { StyleSheet } from 'react-native';

import { TooltipArrow } from '@/assets/icons/tooltipArrow';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components/Box';
import { designSystem, withOpacity } from '@/styles/styles';

import { ds } from './lib/design.system';

export const TOOLTIP_WIDTH = 224;

type FeatureHighlightTooltipProps = {
    title: string;
    description: string;
    onDismiss: () => void;
    onExplore: () => void;
};

/**
 * Tooltip highlighting "NEW" features.
 * Has a title, description, and two "Dismis" and "Explore" buttons.
 */
export const FeatureHighlightTooltip = ({ title, description, onDismiss, onExplore }: FeatureHighlightTooltipProps) => {
    return (
        <Box style={styles.wrapper}>
            <Box style={styles.container}>
                <Box style={styles.content}>
                    <Box style={styles.titleSection}>
                        <Box style={styles.tag}>
                            <Text variant="labelMedium" fontWeight="600" color={ds.colorToken.text.primary}>
                                New
                            </Text>
                        </Box>
                        <Text variant="titleMedium" color={ds.colorToken.text.primary} textAlign="center">
                            {title}
                        </Text>
                    </Box>
                    <Box style={styles.bodySection}>
                        <Text
                            variant="bodySmall"
                            color={ds.colorToken.text.primary}
                            textAlign="center"
                            style={styles.bodyText}
                        >
                            {description}
                        </Text>
                    </Box>
                </Box>
                <Box style={styles.buttons}>
                    <Button
                        label="Dismiss"
                        size="xs"
                        shape="rect"
                        hierarchy="secondary"
                        onPress={onDismiss}
                        style={styles.dismissButton}
                        textStyle={styles.dismissButtonText}
                        testID={'dismiss-btn-tooltip'}
                    />
                    <Button
                        label="Explore"
                        size="xs"
                        shape="rect"
                        hierarchy="primary"
                        onPress={onExplore}
                        style={styles.exploreButton}
                        textStyle={styles.exploreButtonText}
                        testID={'explore-btn-tooltip'}
                    />
                </Box>
            </Box>
            <TooltipArrow />
        </Box>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        alignItems: 'center',
    },
    container: {
        backgroundColor: ds.colorValue.background.brand.strong,
        borderRadius: 24,
        borderCurve: 'continuous',
        padding: 20,
        gap: 16,
        alignItems: 'center',
        width: TOOLTIP_WIDTH,
        shadowColor: withOpacity(designSystem.colors.neutralBlack, 0.6),
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 1,
        shadowRadius: 12,
        elevation: 12,
    },
    content: {
        width: '100%',
    },
    titleSection: {
        alignItems: 'center',
        gap: 6,
    },
    tag: {
        backgroundColor: withOpacity(ds.colorValue.background.neutral.inverse, 0.2),
        borderRadius: 6,
        borderCurve: 'continuous',
        paddingHorizontal: 6,
        paddingVertical: 2,
    },
    bodySection: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    bodyText: {
        opacity: 0.7,
    },
    buttons: {
        flexDirection: 'row',
        gap: 8,
    },
    dismissButton: {
        backgroundColor: withOpacity(ds.colorValue.background.neutral.inverse, 0.2),
        minWidth: 67,
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
    },
    dismissButtonText: {
        color: ds.colorValue.text.primary,
    },
    exploreButton: {
        backgroundColor: ds.colorValue.background.neutral.inverse,
        minWidth: 67,
    },
    exploreButtonText: {
        color: ds.colorValue.background.brand.strong,
    },
});
